/**
 * The ONE semantic compact-viewport constant for the shared shell (#1466 F-30B,
 * ADR F-30 mechanics decision 7).
 *
 * ── what is shared, and what deliberately is not ────────────────────────────
 * `860` appears three times in this repo and it means two different things:
 *
 * | where | meaning |
 * |---|---|
 * | `apps/console/src/app/AppShell.module.css` | the VIEWPORT is compact — the sidebar is an off-canvas drawer |
 * | `shell/DrawerShell.module.css` | the VIEWPORT is compact — the right rail collapses to a launcher |
 * | `shell/WorkspaceSplit.module.css` | the CONTAINER is narrow — a content box stacks its two panes |
 *
 * The first two are the same boundary and are shared here. The third is a
 * `@container` query about a box that can be 700px wide inside a 1600px window,
 * so it answers a different question with the same number and stays local. Rule
 * 42's inverse: a coincidence of value is not a shared fact, and binding them
 * would couple the chrome to a content layout that has every right to move.
 *
 * ── a media query cannot read a TypeScript constant ─────────────────────────
 * So the literal has to exist in the stylesheet, and the seam that keeps the two
 * honest is a test rather than a build step: `conformance/stylesheet.test.ts`
 * reads the media queries in `DrawerShell.module.css` and requires them to equal
 * `SHELL_COMPACT_MAX_PX`, exactly as `apps/console/src/app/breakpoints.test.ts`
 * has done for `AppShell.module.css` since UX-03. That is the whole mechanism —
 * one value, two readers, one assertion.
 */
import { useEffect, useState } from 'react'

/**
 * At or below this width the shell is COMPACT: the console's sidebar is an
 * off-canvas drawer and the shared drawer's rail collapses to a single labelled
 * launcher in the safe-area action zone.
 *
 * 860 rather than any of the other numbers in circulation because the
 * STYLESHEET is what the user sees (UX-03's argument, unchanged).
 */
export const SHELL_COMPACT_MAX_PX = 860

/** The `matchMedia` query for "the shell is currently compact". */
export const SHELL_COMPACT_QUERY = `(max-width: ${SHELL_COMPACT_MAX_PX}px)`

/**
 * Is the shell compact right now?
 *
 * Returns `false` where `matchMedia` does not exist. That is not a default
 * dressed up as an answer — jsdom ships no `matchMedia` and performs no layout,
 * so the honest reading in a unit rig is "this environment has no viewport",
 * and the desktop branch is the one whose semantics a jsdom fixture can
 * actually assert. The compact branch is proved in a browser, at real
 * viewports, in `apps/portal/e2e-browser/shell-responsive.spec.ts`.
 */
export function useShellCompact(): boolean {
  const [compact, setCompact] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(SHELL_COMPACT_QUERY).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(SHELL_COMPACT_QUERY)
    const onChange = () => setCompact(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return compact
}

/**
 * At or below this width the drawer's panel OVERLAYS the canvas (scrim, dialog)
 * instead of DOCKING beside it (#2502 §5.2, R1).
 *
 * The number is derived, not chosen: panel floor 420 + wall 56 + the ruled
 * 560px canvas floor, minus one — `conformance/stylesheet.test.ts` re-derives
 * it from the stylesheet, and `DrawerShell.docked.test.tsx` binds this constant
 * to the literal in `DrawerShell.module.css`, the way `SHELL_COMPACT_MAX_PX` is
 * bound. The JS needs it because two things are decided only where the drawer
 * DOCKS: the host's default lens, and restoring the saved open lens. An
 * overlay or a compact sheet that opens itself on load is a modal nobody asked
 * for — the defect this constant exists to stop.
 */
export const DRAWER_OVERLAY_MAX_PX = 1035

/** The `matchMedia` query for "the drawer overlays rather than docks". */
export const DRAWER_OVERLAY_QUERY = `(max-width: ${DRAWER_OVERLAY_MAX_PX}px)`

/**
 * Does the drawer DOCK right now? Read synchronously, so a state initialiser
 * can use it before the first paint. `true` where `matchMedia` does not exist,
 * for the reason `useShellCompact` gives: jsdom has no viewport, and the
 * desktop branch is the one a unit rig can honestly assert.
 */
export function drawerDockedNow(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return !window.matchMedia(DRAWER_OVERLAY_QUERY).matches
}

/** `drawerDockedNow`, live across a resize. */
export function useDrawerDocked(): boolean {
  const [docked, setDocked] = useState(drawerDockedNow)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(DRAWER_OVERLAY_QUERY)
    const onChange = () => setDocked(!mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return docked
}
